import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CTA from "../components/cta";
import "../components/faq.css";
import "./villas-for-sale-abu-dhabi.css";

const sellingPillars = [
  {
    icon: "fas fa-camera",
    title: "Professional Presentation",
    text: "We provide high-quality photography, virtual tours, and engaging descriptions. We know that the first viewing happens online, so we make sure your home looks spectacular.",
  },
  {
    icon: "fas fa-bullhorn",
    title: "Targeted Exposure",
    text: "We don't wait for buyers to find us. We actively market your home on leading platforms like Bayut, Property Finder, and Dubizzle, alongside our exclusive social media channels.",
  },
  {
    icon: "fas fa-handshake",
    title: "Expert Negotiation",
    text: "Our agents are seasoned professionals who understand the local market trends. We handle the tough conversations to ensure you get a fair deal that reflects the true value of your asset.",
  },
];

const sellingSteps = [
  ["Accurate Valuation", "We provide a data-driven market analysis to help you set a competitive price."],
  ["Strategic Marketing", "Your property is featured in front of qualified local and international buyers."],
  ["Viewing Management", "We handle all inquiries and conduct professional viewings at your convenience."],
  ["Legal Navigation", "From the Memorandum of Understanding (MOU) to obtaining the No Objection Certificate (NOC) from the developer, we guide you through every legal requirement."],
  ["Final Transfer", "We assist with the final ownership transfer through the official TAMM portal, ensuring a smooth hand-over."],
];

const faqs = [
  {
    icon: "fas fa-coins",
    question: "What are the costs when I sell my property in Abu Dhabi?",
    answer: "Typically, the seller pays a 2% brokerage commission and a portion of the transfer fees (usually another 2%, though this can be negotiated with the buyer). You should also account for the developer's NOC fee and any outstanding service charges.",
  },
  {
    icon: "fas fa-hourglass-half",
    question: "How long does it take to sell a property?",
    answer: "On average, a well-priced property in a popular area like Al Reem or Yas Island can sell within 30 to 60 days. However, the exact timeline depends on current market demand and how effectively you list property in Abu Dhabi.",
  },
  {
    icon: "fas fa-plane",
    question: "Do I need to be in the UAE to sell my home?",
    answer: "Not necessarily. If you are abroad, you can grant a Power of Attorney (POA) to a trusted representative or your agent to handle the paperwork and transfer on your behalf.",
  },
  {
    icon: "fas fa-file-contract",
    question: "Is a valuation certificate mandatory?",
    answer: "While not always mandatory for the listing phase, the Department of Municipalities and Transport (DMT) may require a valuation certificate to finalize the transfer of ownership.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: { "@type": "Answer", text: answer },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://nvrealestate.ae/" },
    { "@type": "ListItem", position: 2, name: "Sell My Property in Abu Dhabi", item: "https://nvrealestate.ae/sell-your-property-in-abu-dhabi" },
  ],
};

function SellYourPropertyInAbuDhabi() {
  const [activeFaq, setActiveFaq] = useState(0);

  return (
    <>
      <Helmet>
        <title>Sell My Property in Abu Dhabi | List Property in Abu Dhabi</title>
        <meta
          name="description"
          content="Do you want to sell your property in Abu Dhabi? NV Real Estate helps you get the best price for your property. List your property in Abu Dhabi now."
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <main
        className="villa-page"
        style={{ "--villa-section-image": `url(${process.env.PUBLIC_URL}/assets/images/abu.jpg)` }}
      >
        <section className="villa-hero" aria-labelledby="villa-page-title">
          <div
            className="villa-hero__image"
            aria-hidden="true"
            style={{ backgroundImage: `url("/images/seo/sell-your-property-in-abu-dhabi/01.png")` }}
          />
          <div className="villa-hero__overlay" aria-hidden="true" />
          <div className="container villa-hero__inner">
            <div className="villa-hero__content">
              <span className="villa-eyebrow">Abu Dhabi selling specialists</span>
              <h1 id="villa-page-title">Sell My Property in Abu Dhabi</h1>
              <div className="villa-hero__actions">
                <Link className="villa-button villa-button--primary" to="/contact-us">
                  List your property
                </Link>
                <a className="villa-button villa-button--ghost" href="tel:+971501577822">
                  Call +971 50 157 7822
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="villa-intro villa-section">
          <div className="container villa-intro__grid">
            <div className="villa-intro__image">
              <img
                src="/images/seo/sell-your-property-in-abu-dhabi/02.png"
                alt="Sell your property in Abu Dhabi"
              />
              <div className="villa-intro__note">
                <strong>Trusted realtors</strong>
                <span>Helping you sell with confidence</span>
              </div>
            </div>
            <div className="villa-section-heading villa-intro__content">
              <span className="villa-kicker">Nova Vision Real Estate</span>
              <h2>Sell Your Property in Abu Dhabi with Confidence – NV Real Estate</h2>
              <p>
                Are you thinking, "It's time to sell my property in Abu Dhabi," but don't know
                where to start? In a competitive market like the UAE capital, having the right
                partner is the difference between a listing that sits and a deal that closes. NV
                Real Estate provides a premium, end-to-end service designed to help you sell
                property in Abu Dhabi quickly, efficiently, and for the best possible price.
              </p>
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-benefits--centered-cards villa-section" aria-labelledby="why-list-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Why list with us</span>
              <h2 id="why-list-heading">Why List Your Property with NV Real Estate?</h2>
              <p>
                When you choose to list property in Abu Dhabi with us, you aren't just putting a
                "For Sale" sign on your door. You are gaining access to a sophisticated marketing
                engine and a network of high-net-worth investors. Our approach is built on three
                main pillars:
              </p>
            </div>
            <div className="villa-benefits__grid">
              {sellingPillars.map((item, index) => (
                <article className="villa-benefit-card" key={item.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={item.icon} /></span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="villa-process villa-section" aria-labelledby="villa-process-title">
          <div className="container villa-process__layout">
            <div className="villa-process__aside">
              <span className="villa-kicker">Our proven selling process</span>
              <h2 id="villa-process-title">Our Proven Selling Process</h2>
              <p>
                At NV Real Estate, we take pride in our "Experience, Expertise,
                Authoritativeness, and Trustworthiness." We simplify the legal and financial
                steps so you can focus on your next move. Here is how we help you sell property
                in Abu Dhabi:
              </p>
              <Link className="villa-text-link" to="/contact-us">Speak with an advisor <span aria-hidden="true">-&gt;</span></Link>
            </div>
            <div className="villa-process__steps-wrap">
              <ol className="villa-process__steps">
                {sellingSteps.map(([title, text], index) => (
                  <li key={title}>
                    <span className="villa-process__number">{String(index + 1).padStart(2, "0")}</span>
                    <div><h3>{title}</h3><p>{text}</p></div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section className="nv-faq-section villa-faq" aria-labelledby="villa-faq-title">
          <div className="nv-faq-container">
            <header className="nv-faq-heading">
              <span className="nv-faq-label">Seller questions</span>
              <h2 id="villa-faq-title">Frequently Asked Questions</h2>
              <p>Practical answers to help you sell your property in Abu Dhabi with confidence.</p>
            </header>

            <div className="villa-faq__main">
              <div className="nv-faq-accordion">
                {faqs.map((faq, index) => {
                  const isActive = activeFaq === index;
                  return (
                    <article className={`nv-faq-item${isActive ? " active" : ""}`} key={faq.question}>
                      <button
                        className="nv-faq-question"
                        type="button"
                        onClick={() => setActiveFaq((current) => (current === index ? null : index))}
                        aria-expanded={isActive}
                        aria-controls={`sell-faq-answer-${index}`}
                      >
                        <span className="nv-faq-question-icon"><i className={faq.icon} /></span>
                        <strong>{faq.question}</strong>
                        <span className="nv-faq-toggle" aria-hidden="true" />
                      </button>
                      <div
                        className="nv-faq-answer"
                        id={`sell-faq-answer-${index}`}
                        aria-hidden={!isActive}
                      >
                        <p>{faq.answer}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            <div className="nv-faq-trust-strip">
              <div className="nv-faq-trust-item"><i className="fas fa-camera" /><div><strong>Pro Presentation</strong><span>Photos &amp; Virtual Tours</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-bullhorn" /><div><strong>Targeted Exposure</strong><span>Bayut, Property Finder &amp; More</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-file-signature" /><div><strong>Legal Navigation</strong><span>MOU to NOC to Transfer</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-headset" /><div><strong>Dedicated Advisor</strong><span>One Point of Contact</span></div></div>
            </div>
            <p className="villa-faq__contact">
              Call us now at <a href="tel:+971501577822">+971-501577822</a>. You can also email us
              now at <a href="mailto:info@nvrealestate.ae">info@nvrealestate.ae</a>.
            </p>
          </div>
        </section>
      </main>

      <CTA />

      <style>{`
        .villa-benefits--centered-cards .villa-benefits__grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 24px;
        }
        .villa-benefits--centered-cards .villa-benefit-card {
          flex: 1 1 260px;
          max-width: 300px;
          text-align: center;
        }
        .villa-benefits--centered-cards .villa-benefit-card__icon,
        .villa-benefits--centered-cards .villa-benefit-card__number {
          margin-left: auto;
          margin-right: auto;
        }
        @media (max-width: 700px) {
          .villa-benefits--centered-cards .villa-benefit-card {
            flex: 1 1 100%;
            max-width: 420px;
          }
        }
        .villa-process__steps {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 24px;
        }
        .villa-process__steps li {
          flex: 1 1 260px;
          max-width: 300px;
        }
        @media (max-width: 700px) {
          .villa-process__steps li {
            flex: 1 1 100%;
            max-width: 420px;
          }
        }
      `}</style>
    </>
  );
}

export default SellYourPropertyInAbuDhabi;